import { procurementApi, supplierApi } from "@/main/api";
import useAsyncModalConfirm from "@/main/components/shared/asyncModalConfirm/asyncModalConfirm";
import { KeyValueDefault } from "@/main/models/common";
import { GetReassignItemsFilterRequest } from "@/main/models/request";
import { RoutesConstants } from "@/main/routes/routesConstants";
import SupplierIgnoreConfirm from "@/main/screens/order/reassignOrder/components/supplierIgnoreConfirm";
import { loadReassign } from "@/main/screens/order/reassignOrder/reassignLoaders";
import {
	buildReassignFormSchema,
	getAnswerPrice,
	getReassignModeEnum,
	PartFormValue,
	ReassignForm,
	ReassignModeEnum,
	ReassignPartData,
} from "@/main/screens/order/reassignOrder/reassignScreenUtils";
import {
	buildPartTypeSupplierIgnoreRequest,
	buildReassignRequests,
} from "@/main/screens/order/reassignOrder/reassignSubmit";
import { Controls } from "@/main/services/constants/constantsControl";
import { PartTypeEnum } from "@/main/services/enum/PartTypeEnum";
import { ReasonTypeEnum } from "@/main/services/enum/ReasonEnum";
import { isNullUndefined } from "@/main/services/reducerUtils";
import {
	getSessionStorage,
	removeKeySessionStorage,
	SELECTED_CANCELLED_PARTS_REASSIGN,
	SelectedCancelledPartsReassign,
} from "@/main/services/storage";
import { getDefaultCountryOptionDrop, getTranslateByKey, isShowControlByElementId } from "@/main/services/utils";
import { formatCurrency } from "@/main/services/utilsNumber";
import { getRefreshForceUrl } from "@/main/services/utilsQueryParams";
import { getCountriesUser, useCountryReducer } from "@/main/store/country";
import { getReason } from "@/main/store/reason";
import { zodResolver } from "@hookform/resolvers/zod";
import { createElement, useEffect, useMemo, useState } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { useDispatch } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";

const TITLE_KEY_BY_MODE: Record<ReassignModeEnum, string> = {
	[ReassignModeEnum.Single]: "screen.reassignOrder",
	[ReassignModeEnum.Bulk]: "information.bulkReassign",
	[ReassignModeEnum.Cancelled]: "information.bulkReassignCancelledParts",
};

function toPartFormValue(
	p: ReassignPartData,
	partTypeOptionsForQuote: KeyValueDefault[],
	mode: ReassignModeEnum,
): PartFormValue {
	const isBatchMode = mode !== ReassignModeEnum.Single;

	return {
		guidProcurementSupplierItem: p.guidOriginalProcurementSupplierItem,
		quoteGuid: p.quoteGuid ?? "",
		supplierItems: p.itemAnswers.map((a) => {
			const matchingPartType = partTypeOptionsForQuote.find((pt) => String(pt.default) === String(a.idPartType));
			return {
				guidQuoteSupplierItem: a.guidAnsweredQuoteSupplierItem,
				partType: matchingPartType ?? {
					value: "",
					default: String(a.idPartType ?? ""),
				},
				deliveryDeadline: String(a.deliveryDays),
				price: formatCurrency(getAnswerPrice(a)),
			};
		}),
		selectedGuidQuoteSupplierItem: isBatchMode
			? null
			: (p.itemAnswers.find((a) => a.isOriginalSelected)?.guidAnsweredQuoteSupplierItem ?? null),
		directAssign: null,
		reason: null,
		reasonDescription: "",
	};
}

export default function useReassignScreen(isBulk: boolean) {
	// #region Hooks
	const [getConfirmation, ModalConfirmation] = useAsyncModalConfirm();
	const { countriesUser: countryReducer } = useCountryReducer();
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	// #endregion

	// #region Query Params
	const guidQuoteParams = searchParams.get("guidQuote") || null;
	const guidQuoteProcurementParams = searchParams.get("guidQuoteProcurement") || null;
	const guidClaimParams = searchParams.get("guidClaim") || null;
	const guidProcurementSupplierItemParams = searchParams.get("guidProcurementSupplierItem") || null;
	const previousRouteParam = searchParams.get("previousRoute");
	const cancelledPartsMode = searchParams.get("cancelledParts") || false;
	const canceledBySupplierParam = searchParams.get("canceledBySupplier") === "true";
	// #endregion

	// #region Mode derivation
	const mode = getReassignModeEnum(isBulk, Boolean(cancelledPartsMode));
	const isCanceledBySupplier = mode === ReassignModeEnum.Cancelled || canceledBySupplierParam;
	// #endregion

	// #region State
	const [isLoadingGet, setIsLoadingGet] = useState(true);
	const [isLoadingSave, setIsLoadingSave] = useState(false);
	const [partsData, setPartsData] = useState<ReassignPartData[]>([]);
	const [guidCompanyBuyer, setGuidCompanyBuyer] = useState<string>();
	const [vehicleFullName, setVehicleFullName] = useState<string>();
	const [possibleTotalLoss, setPossibleTotalLoss] = useState<boolean>();
	const [messageErrorScreen, setMessageErrorScreen] = useState<string | null>(null);
	const [partTypeOptionsByQuote, setPartTypeOptionsByQuote] = useState<Record<string, KeyValueDefault[]>>({});
	// #endregion

	// #region Form
	const formSchema = useMemo(() => buildReassignFormSchema(mode), [mode]);

	const methods = useForm<ReassignForm>({
		mode: "onSubmit",
		reValidateMode: "onChange",
		resolver: zodResolver(formSchema),
		defaultValues: { parts: [] },
	});

	const { replace } = useFieldArray({
		control: methods.control,
		name: "parts",
	});
	// #endregion

	// #region Computed
	const showPrices = isShowControlByElementId(Controls.IdShowPrices);
	const showEditControl = isShowControlByElementId(Controls.IdEditReassign);
	const showAutofill = mode !== ReassignModeEnum.Single;
	const showImagesButton = !isCanceledBySupplier;
	const showCanceledBySupplierWarning = mode !== ReassignModeEnum.Cancelled;

	const titleScreen = `${getTranslateByKey("information.order")} > ${getTranslateByKey(TITLE_KEY_BY_MODE[mode])}`;
	// #endregion

	// #region Error messages
	const ERROR_QUOTE_NOT_FOUND = getTranslateByKey("information.quoteNotFound", true);
	// #endregion

	async function loadReassignData() {
		try {
			setMessageErrorScreen(null);
			const partsSelectedData =
				mode === ReassignModeEnum.Cancelled
					? getSessionStorage<SelectedCancelledPartsReassign>(SELECTED_CANCELLED_PARTS_REASSIGN)
					: null;
			const request: GetReassignItemsFilterRequest = {
				canceledBySupplier: isCanceledBySupplier,
				guidClaim: guidClaimParams,
				guidProcurement: guidQuoteProcurementParams,
				guidProcurementSupplierItems:
					partsSelectedData?.procurementSupplierItemGuids ??
					(guidProcurementSupplierItemParams ? [String(guidProcurementSupplierItemParams)] : null),
			};
			const guidProcurementHeader = guidQuoteProcurementParams;
			setIsLoadingGet(true);
			const cancelledQuoteGuids = partsSelectedData?.quoteGuids;
			const cancelledPartsInfo = partsSelectedData?.partsInfo;
			const result = await loadReassign(
				request,
				guidProcurementHeader,
				guidClaimParams,
				cancelledQuoteGuids,
				cancelledPartsInfo,
			);

			const getPartDisplayName = (part: ReassignPartData) => {
				const info = part.quoteProcurementPartInfo;
				const isTireGlass =
					info?.idPartTypeQuoteItem === PartTypeEnum.Tire || info?.idPartTypeQuoteItem === PartTypeEnum.Glass;

				if (isTireGlass) {
					return [info?.quoteItemDescription, info?.notes ? info.notes : info?.partTypeQuoteItem]
						.filter(Boolean)
						.join(" - ");
				}

				const primaryAnswer = part.itemAnswers.find((a) => a.isOriginalSelected) || part.itemAnswers[0];

				return primaryAnswer?.quoteItemDescription || info?.quoteItemDescription || "";
			};

			const sortedParts = [...result.parts].sort((a, b) => {
				return getPartDisplayName(a).localeCompare(getPartDisplayName(b));
			});

			setPartsData(sortedParts);
			setGuidCompanyBuyer(result.guidCompanyBuyer || partsSelectedData?.guidCompanyBuyer);
			setVehicleFullName(result.vehicleFullName);
			setPossibleTotalLoss(result.possibleTotalLoss);
			setPartTypeOptionsByQuote(result.partTypeOptionsByQuote);
			replace(sortedParts.map((p) => toPartFormValue(p, result.partTypeOptionsByQuote[p.quoteGuid ?? ""] ?? [], mode)));
			setIsLoadingGet(false);
		} catch (error) {
			setMessageErrorScreen(ERROR_QUOTE_NOT_FOUND);
			console.error(error);
		} finally {
			setIsLoadingGet(false);
		}
	}

	// #region Load
	useEffect(() => {
		loadReassignData();
	}, [
		mode,
		guidQuoteParams,
		guidQuoteProcurementParams,
		guidClaimParams,
		guidProcurementSupplierItemParams,
		canceledBySupplierParam,
	]);

	useEffect(() => {
		if (isNullUndefined(countryReducer.data)) dispatch(getCountriesUser());
		const countryUser = getDefaultCountryOptionDrop(countryReducer.data);
		if (!countryUser) return;

		dispatch(
			getReason({
				guidCountry: String(countryUser.key),
				idReasonType: ReasonTypeEnum.Reassing,
			}),
		);
	}, [countryReducer.data]);
	// #endregion

	// #region Submit
	async function onSubmit(data: ReassignForm) {
		const ignoreRequest = buildPartTypeSupplierIgnoreRequest(data, partsData);
		if (ignoreRequest.partTypeSupplierIgnores.length > 0) {
			const response = await supplierApi.getPartTypeSupplierIgnore(ignoreRequest);
			const resultData = response?.data?.result ?? [];
			if (resultData.length > 0) {
				const isConfirmIgnore = await getConfirmation(createElement(SupplierIgnoreConfirm, { resultData }));
				if (!isConfirmIgnore) return;
			}
		}

		const message = getTranslateByKey("information.areYouSureYouWantToReassignTheParts");
		const isConfirmed = await getConfirmation(message);
		if (!isConfirmed) return;

		setIsLoadingSave(true);

		const request = buildReassignRequests(data);

		procurementApi
			.reassignPartsCancelledBySupplier(request)
			.then((res) => {
				const { result } = res.data;
				if (result) {
					const successKey =
						mode === ReassignModeEnum.Single
							? "information.supplierTransferedSuccessfully"
							: "information.partsReassignedSuccessfully";
					toast.success(getTranslateByKey(successKey));
					handleReassignSuccess();
				}
			})
			.finally(() => {
				setIsLoadingSave(false);
			});
	}

	function handleBack() {
		if (mode === ReassignModeEnum.Cancelled) {
			navigate(-1);
			removeKeySessionStorage(SELECTED_CANCELLED_PARTS_REASSIGN);
			return;
		}
		if (previousRouteParam) {
			navigate(`${previousRouteParam}`);
			return;
		}
		if (guidClaimParams) {
			navigate(RoutesConstants.claimSearchClaims);
			return;
		}
		navigate(RoutesConstants.orderSearchOrder);
	}

	function handleReassignSuccess() {
		if (mode === ReassignModeEnum.Cancelled) {
			navigate(-1);
			removeKeySessionStorage(SELECTED_CANCELLED_PARTS_REASSIGN);
			return;
		}

		const claimNumber = partsData[0]?.claimNumber;
		const isReturningToClaims = previousRouteParam === RoutesConstants.claimSearchClaims || Boolean(guidClaimParams);

		if (isReturningToClaims) {
			navigate(`${RoutesConstants.claimSearchClaims}${getRefreshForceUrl({ claimNumber })}`);
			return;
		}

		navigate(
			`${RoutesConstants.orderSearchOrder}${getRefreshForceUrl({
				quoteFilterVehicle: claimNumber,
			})}`,
		);
	}
	// #endregion

	return {
		isLoadingGet,
		isLoadingSave,
		messageErrorScreen,
		partsData,
		guidCompanyBuyer,
		vehicleFullName,
		possibleTotalLoss,
		partTypeOptionsByQuote,
		guidClaimParams,
		methods,
		titleScreen,
		showPrices,
		showEditControl,
		showAutofill,
		showImagesButton,
		showCanceledBySupplierWarning,
		onSubmit,
		handleBack,
		ModalConfirmation,
	};
}
